/**
 * Carte admin « Diffuser ce défi » — filme la CARTE du défi (média élève +
 * question + choix) en 9:16 via le pipeline YouTube Studio et la publie en
 * YouTube Short et/ou TikTok. Aperçu de validation puis publication.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  Youtube,
  Video,
  Film,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import {
  buildReelOverlay,
  buildChallengeShortCaption,
  challengeShareUrl,
  buildYoutubeTitle,
} from '@jeraisonne/db/youtube';
import { useAuth } from '@/lib/auth';
import { useSocialAccount, useYoutubeVideo } from '@/lib/admin/youtube-queries';
import { useStartSocialOAuth } from '@/lib/admin/use-social';
import { jobStepLabel, VIDEO_STATUS_LABEL } from '@/lib/admin/youtube-labels';
import { MusicPicker } from '@/features/youtube-studio/asset-pickers';
import { useReelPreviewUrl } from '@/features/exercise-reel/use-exercise-reel';
import {
  useCreateChallengeReel,
  usePublishChallengeReel,
  type ChallengeReelPlatform,
  type ChallengeDiffusionData,
} from './use-challenge-reel';

const fieldCls =
  'w-full rounded-xl border border-ink-200 bg-white px-3 py-2 font-sans text-sm text-ink-800 outline-none focus:border-primary-300';

const PLATFORM_META: Record<
  ChallengeReelPlatform,
  { label: string; account: 'youtube' | 'tiktok'; Icon: typeof Youtube }
> = {
  youtube: { label: 'YouTube Short', account: 'youtube', Icon: Youtube },
  tiktok: { label: 'TikTok', account: 'tiktok', Icon: Video },
};

export function ChallengeReelCard({ challenge }: { challenge: ChallengeDiffusionData }) {
  const { user } = useAuth();
  const levelLabel = SCHOOL_LEVELS.find((l) => l.value === challenge.level)?.label ?? '';
  const subjectLabel = SUBJECTS.find((s) => s.value === challenge.subject)?.label ?? '';
  const challengeUrl = challengeShareUrl(challenge.slug);

  const yt = useSocialAccount('youtube');
  const tk = useSocialAccount('tiktok');
  const connected: Record<ChallengeReelPlatform, boolean> = {
    youtube: !!yt.data,
    tiktok: !!tk.data,
  };
  const startOAuth = useStartSocialOAuth();
  const create = useCreateChallengeReel();

  const [platforms, setPlatforms] = useState<ChallengeReelPlatform[]>(['youtube']);
  const [target, setTarget] = useState('');
  const [caption, setCaption] = useState('');
  const [musicAssetId, setMusicAssetId] = useState<string | null>(null);
  const [createdVideoId, setCreatedVideoId] = useState<string | null>(null);

  // Légende auto (une fois), éditable ensuite — typo des posts d'articles.
  const captionSeeded = useRef(false);
  useEffect(() => {
    if (captionSeeded.current) return;
    captionSeeded.current = true;
    setCaption(buildChallengeShortCaption({ title: challenge.title, challengeUrl }));
  }, [challenge.title, challengeUrl]);

  const togglePlatform = (p: ChallengeReelPlatform) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  async function handleGenerate() {
    if (!user || platforms.length === 0) return;
    const overlay = buildReelOverlay({
      title: challenge.title,
      level: levelLabel,
      subject: subjectLabel,
      target,
      exerciseId: challenge.exercise_id,
    });
    const id = await create.mutateAsync({
      challengeId: challenge.id,
      exerciseId: challenge.exercise_id,
      attemptId: challenge.attempt_id,
      userId: user.id,
      overlay,
      caption,
      ytTitle: buildYoutubeTitle({ titre: challenge.title, niveau: levelLabel }),
      ytDescription: `${caption}\n\n#Shorts`,
      musicAssetId,
      platforms,
    });
    setCreatedVideoId(id);
  }

  const connectTikTok = async () => {
    const { url } = await startOAuth.mutateAsync('tiktok');
    window.location.href = url;
  };

  const canGenerate = !!user && platforms.length > 0 && !create.isPending;
  // Publier exige que chaque plateforme choisie soit connectée.
  const missing = platforms.filter((p) => !connected[p]);

  return (
    <div className="border-ink-200 mb-6 rounded-2xl border bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <Film className="text-primary-500 h-4 w-4" />
        <h2 className="font-display text-ink-900 text-base font-extrabold">Diffuser ce défi</h2>
      </div>
      <p className="text-ink-500 mb-4 font-sans text-xs">
        Filme la carte du défi (média de l'élève, question, choix) en vidéo verticale et publie-la
        en Short / TikTok. Aperçu à valider avant publication.
      </p>

      {/* Sélection des plateformes + statut de connexion */}
      <div className="mb-4 space-y-2">
        {(Object.keys(PLATFORM_META) as ChallengeReelPlatform[]).map((p) => {
          const meta = PLATFORM_META[p];
          const isOn = platforms.includes(p);
          const account = meta.account === 'youtube' ? yt : tk;
          return (
            <div
              key={p}
              className={cn(
                'flex items-center gap-3 rounded-xl border p-3',
                isOn ? 'border-primary-200 bg-primary-50' : 'border-ink-200 bg-white',
              )}
            >
              <input
                type="checkbox"
                checked={isOn}
                onChange={() => togglePlatform(p)}
                className="h-5 w-5 shrink-0"
                aria-label={meta.label}
              />
              <meta.Icon className="text-ink-500 h-4 w-4 shrink-0" />
              <span className="font-display text-ink-800 flex-1 text-sm font-bold">
                {meta.label}
              </span>
              {account.isLoading ? (
                <span className="text-ink-400 text-xs">…</span>
              ) : connected[p] ? (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connecté
                </span>
              ) : p === 'tiktok' ? (
                <button
                  type="button"
                  onClick={() => void connectTikTok()}
                  disabled={startOAuth.isPending}
                  className="border-ink-200 text-ink-700 inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border bg-white px-2.5 text-xs font-bold active:scale-[.98] disabled:opacity-50"
                >
                  {startOAuth.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  Connecter
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5" /> Non connecté
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-4">
        {/* Cible / accroche (incrustée sur la cover) */}
        <label className="block">
          <span className="text-ink-600 mb-1 block font-sans text-xs font-bold">
            Accroche (incrustée sur la cover)
          </span>
          <input
            className={fieldCls}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Sauras-tu faire mieux ?"
            maxLength={160}
          />
        </label>

        {/* Musique (optionnelle) */}
        <div>
          <span className="text-ink-600 mb-1 block font-sans text-xs font-bold">
            Musique (optionnelle)
          </span>
          <MusicPicker selectedId={musicAssetId} onSelect={setMusicAssetId} />
        </div>

        {/* Légende */}
        <label className="block">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-ink-600 font-sans text-xs font-bold">
              Légende (TikTok / description)
            </span>
            <button
              type="button"
              onClick={() =>
                setCaption(buildChallengeShortCaption({ title: challenge.title, challengeUrl }))
              }
              className="text-primary-600 inline-flex items-center gap-1 text-[11px] font-bold"
            >
              <RotateCcw className="h-3 w-3" /> Régénérer
            </button>
          </div>
          <textarea
            className={cn(fieldCls, 'min-h-28 resize-y text-[13px]')}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={2200}
          />
          <span className="text-ink-400 mt-1 block text-xs">{caption.length}/2200</span>
        </label>

        {create.error && (
          <p className="text-accent-600 font-sans text-sm">{(create.error as Error).message}</p>
        )}

        {!createdVideoId && (
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={!canGenerate}
            className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50 sm:w-auto"
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Film className="h-4 w-4" />
            )}
            Générer l'aperçu
          </button>
        )}

        {platforms.length === 0 && (
          <p className="text-ink-400 font-sans text-xs">⚠️ Choisis au moins une plateforme.</p>
        )}

        {createdVideoId && (
          <ChallengeReelReview
            videoId={createdVideoId}
            platforms={platforms}
            missing={missing}
            onRefaire={() => setCreatedVideoId(null)}
          />
        )}
      </div>
    </div>
  );
}

/** Aperçu + validation : suit le rendu, montre la vidéo et permet de publier. */
function ChallengeReelReview({
  videoId,
  platforms,
  missing,
  onRefaire,
}: {
  videoId: string;
  platforms: ChallengeReelPlatform[];
  missing: ChallengeReelPlatform[];
  onRefaire: () => void;
}) {
  const { data } = useYoutubeVideo(videoId, { live: true });
  const { data: previewUrl } = useReelPreviewUrl(videoId);
  const publish = usePublishChallengeReel();

  if (!data) return null;
  const { video, job, publications } = data;
  const targetPubs = publications.filter((p) => (platforms as string[]).includes(p.platform));
  const failed =
    video.status === 'failed' ||
    job?.status === 'failed' ||
    targetPubs.some((p) => p.status === 'failed');
  const published = targetPubs.length > 0 && targetPubs.every((p) => p.status === 'published');
  const publishing =
    !published && targetPubs.some((p) => p.status === 'queued' || p.status === 'uploading');
  const previewReady = !failed && !published && !publishing && !!previewUrl;

  if (published) {
    return (
      <div className="mt-2 space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800">
          <CheckCircle2 className="h-4 w-4" /> Enregistré
        </span>
        <div className="space-y-1.5">
          {targetPubs.map((p) => (
            <div key={p.platform} className="flex items-center gap-2 text-sm">
              <span className="font-medium text-emerald-800">
                {PLATFORM_META[p.platform as ChallengeReelPlatform]?.label ?? p.platform}
              </span>
              {p.external_url && (
                <a
                  href={p.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 ml-auto inline-flex items-center gap-1 text-xs font-bold"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Voir
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (failed) {
    const err = targetPubs.find((p) => p.error)?.error ?? job?.error;
    return (
      <div className="border-accent-200 bg-accent-50 mt-2 space-y-2 rounded-xl border p-3">
        <span className="text-accent-700 inline-flex items-center gap-1.5 text-sm font-bold">
          <AlertCircle className="h-4 w-4" /> Échec
        </span>
        {err && <p className="text-accent-600 font-sans text-xs">{err}</p>}
        <button
          type="button"
          onClick={onRefaire}
          className="border-ink-200 text-ink-700 inline-flex h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-bold active:scale-[.98]"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Refaire
        </button>
      </div>
    );
  }

  if (previewReady) {
    const canPublish = missing.length === 0 && !publish.isPending;
    return (
      <div className="border-ink-200 mt-2 space-y-3 rounded-xl border bg-white p-3">
        <p className="font-display text-ink-900 text-sm font-bold">
          Aperçu — valide avant publication
        </p>
        <video
          src={previewUrl!}
          controls
          playsInline
          className="aspect-9/16 mx-auto w-full max-w-[240px] rounded-lg bg-black"
        />
        {publish.error && (
          <p className="text-accent-600 font-sans text-xs">{(publish.error as Error).message}</p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => void publish.mutateAsync({ videoId, platforms })}
            disabled={!canPublish}
            className="bg-accent-400 font-display hover:bg-accent-500 inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold text-white shadow-md transition-all active:scale-[.98] disabled:opacity-50"
          >
            {publish.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Film className="h-4 w-4" />
            )}
            Publier
          </button>
          <button
            type="button"
            onClick={onRefaire}
            className="border-ink-200 text-ink-700 inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border bg-white px-4 text-sm font-bold active:scale-[.98]"
          >
            <RotateCcw className="h-4 w-4" /> Refaire
          </button>
        </div>
        {missing.length > 0 && (
          <p className="text-ink-400 font-sans text-xs">Connecte d'abord : {missing.join(', ')}.</p>
        )}
      </div>
    );
  }

  return (
    <div className="border-ink-200 mt-2 rounded-xl border bg-white p-3">
      <span className="text-ink-700 inline-flex items-center gap-2 text-sm font-bold">
        <Loader2 className="text-primary-500 h-4 w-4 animate-spin" />
        {publishing
          ? 'Publication…'
          : (jobStepLabel(job?.current_step) ??
            VIDEO_STATUS_LABEL[video.status] ??
            'Génération de l’aperçu…')}
      </span>
    </div>
  );
}
